// Lazy loading Sentry to reduce main bundle size
let Sentry = null;

const loadSentry = async () => {
  if (!Sentry) {
    const sentryReact = await import(/* webpackChunkName: 'sentry' */ '@sentry/react');
    Sentry = sentryReact.default || sentryReact;
    const browserTracingIntegration = sentryReact.browserTracingIntegration;
    return { Sentry, browserTracingIntegration };
  }
  return { Sentry, browserTracingIntegration: null };
};

export const initSentry = async () => {
  // Only initialize Sentry in production
  if (import.meta.env.MODE === 'production') {
    const { Sentry: SentryModule, browserTracingIntegration } = await loadSentry();
    SentryModule.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: browserTracingIntegration
        ? [
            browserTracingIntegration({
              tracePropagationTargets: ['localhost', /^https:\/\/yourapi\.domain\.com\/api/],
            }),
          ]
        : [],

      // Performance Monitoring
      tracesSampleRate: 0.1, // Capture 10% of the transactions

      // Error filtering
      beforeSend(event) {
        // Filter out development/test errors
        if (event.exception) {
          const error = event.exception.values[0];
          if (error?.value?.includes('ResizeObserver loop limit exceeded')) {
            return null; // Ignore this common benign error
          }
        }
        return event;
      },

      // Environment and release info
      environment: import.meta.env.VITE_ENVIRONMENT || 'production',
      release: import.meta.env.VITE_VERSION || '1.0.0',

      // Additional context
      initialScope: {
        tags: {
          component: 'virtual-hardhat-dashboard',
        },
      },
    });
  }
};

// Custom error boundary component - lazy loaded
export const getSentryErrorBoundary = async () => {
  if (import.meta.env.MODE === 'production') {
    const { Sentry: SentryModule } = await loadSentry();
    return SentryModule.ErrorBoundary;
  }
  return ({ children }) => children; // Return children directly in development
};

// Performance monitoring utilities - using lazy-loaded Sentry
export const startTransaction = async (name, op = 'navigation') => {
  if (import.meta.env.MODE === 'production') {
    const { Sentry: SentryModule } = await loadSentry();
    return SentryModule.startSpan({ name, op }, () => {});
  }
  return { finish: () => {} }; // Mock for development
};

export const captureException = async (error, context = {}) => {
  if (import.meta.env.MODE === 'production') {
    const { Sentry: SentryModule } = await loadSentry();
    SentryModule.captureException(error, { extra: context });
  }
};

export const captureMessage = async (message, level = 'info') => {
  if (import.meta.env.MODE === 'production') {
    const { Sentry: SentryModule } = await loadSentry();
    SentryModule.captureMessage(message, level);
  }
};

export const setUser = async (user) => {
  if (import.meta.env.MODE === 'production') {
    const { Sentry: SentryModule } = await loadSentry();
    SentryModule.setUser({
      id: user.uid,
      email: user.email,
      role: user.role,
    });
  }
};
